/**
 * Safe numeric helpers and normalizers for workflow email Statistics.
 * Zero denominators must never produce NaN or Infinity.
 *
 * Production denominator rules (Mailgun-style):
 * - Total: absolute count
 * - Delivered / Accepted / Rejected / Bounced / Soft / Hard / Failed: % of Total
 * - Opened / Clicked / Replied / Unsubscribed / Complained: % of Delivered
 */

export const EMPTY_EMAIL_STATS = Object.freeze({
  total: 0,
  accepted: 0,
  rejected: 0,
  delivered: 0,
  opened: 0,
  clicked: 0,
  replied: 0,
  bounced: 0,
  softBounced: 0,
  hardBounced: 0,
  unsubscribed: 0,
  complained: 0,
  failed: 0,
  timeline: [],
  recipients: [],
});

export const EMAIL_DETAIL_TABS = [
  { id: 'delivered', label: 'Delivered' },
  { id: 'opened', label: 'Opened' },
  { id: 'clicked', label: 'Clicked' },
  { id: 'replied', label: 'Replied' },
  { id: 'hardBounced', label: 'Hard Bounced' },
  { id: 'softBounced', label: 'Soft Bounced' },
  { id: 'complained', label: 'Complained' },
  { id: 'unsubscribed', label: 'Unsubscribed' },
  { id: 'failed', label: 'Failed' },
];

export const EMAIL_RECIPIENT_TABLE_HEADERS = [
  'Full Name',
  'Email',
  'Status',
  'Last Updated',
];

/** Metric card order for compact side-panel and full summary. */
export const EMAIL_METRIC_CARD_DEFS = [
  { id: 'total', label: 'Total', countKey: 'total', rateOf: null, viewDetails: true },
  { id: 'delivered', label: 'Delivered', countKey: 'delivered', rateOf: 'total', viewDetails: true },
  { id: 'opened', label: 'Opened', countKey: 'opened', rateOf: 'delivered', viewDetails: true },
  { id: 'clicked', label: 'Clicked', countKey: 'clicked', rateOf: 'delivered', viewDetails: true },
  { id: 'replied', label: 'Replied', countKey: 'replied', rateOf: 'delivered', viewDetails: true },
  { id: 'bounced', label: 'Bounced', countKey: 'bounced', rateOf: 'total', viewDetails: true },
  { id: 'unsubscribed', label: 'Unsubscribed', countKey: 'unsubscribed', rateOf: 'delivered', viewDetails: true },
  { id: 'accepted', label: 'Accepted', countKey: 'accepted', rateOf: 'total', viewDetails: false },
  { id: 'rejected', label: 'Rejected', countKey: 'rejected', rateOf: 'total', viewDetails: false },
  { id: 'complained', label: 'Complained', countKey: 'complained', rateOf: 'delivered', viewDetails: false },
];

/** Engagement summary row defs used in full dashboard. */
export const EMAIL_ENGAGEMENT_SUMMARY_DEFS = [
  { id: 'delivered', label: 'Delivered', countKey: 'delivered', rateOf: 'total' },
  { id: 'opened', label: 'Opened', countKey: 'opened', rateOf: 'delivered' },
  { id: 'clicked', label: 'Clicked', countKey: 'clicked', rateOf: 'delivered' },
  { id: 'softBounced', label: 'Soft Bounced', countKey: 'softBounced', rateOf: 'total' },
  { id: 'hardBounced', label: 'Hard Bounced', countKey: 'hardBounced', rateOf: 'total' },
  { id: 'unsubscribed', label: 'Unsubscribed', countKey: 'unsubscribed', rateOf: 'delivered' },
  { id: 'spam', label: 'Spam', countKey: 'complained', rateOf: 'delivered' },
];

/** Compact panel cards (Send Email Statistics tab). */
export const EMAIL_COMPACT_METRIC_DEFS = [
  { id: 'total', label: 'Total', countKey: 'total', rateOf: null },
  { id: 'delivered', label: 'Delivered', countKey: 'delivered', rateOf: 'total' },
  { id: 'opened', label: 'Opened', countKey: 'opened', rateOf: 'delivered' },
  { id: 'clicked', label: 'Clicked', countKey: 'clicked', rateOf: 'delivered' },
  { id: 'replied', label: 'Replied', countKey: 'replied', rateOf: 'delivered' },
  { id: 'bounced', label: 'Bounced', countKey: 'bounced', rateOf: 'total' },
  { id: 'unsubscribed', label: 'Unsubscribed', countKey: 'unsubscribed', rateOf: 'delivered' },
];

export function safeNumber(value) {
  const number = Number(value);
  return Number.isFinite(number) ? number : 0;
}

export function safeRate(value, denominator) {
  const numerator = safeNumber(value);
  const total = safeNumber(denominator);
  if (total <= 0) return 0;
  return (numerator / total) * 100;
}

export function formatRate(value) {
  return `${safeNumber(value).toFixed(2)}%`;
}

export function formatCount(value) {
  return String(safeNumber(value));
}

export function formatRateOrWhole(value) {
  const number = safeNumber(value);
  if (Number.isInteger(number) || Math.abs(number - Math.round(number)) < 1e-9) {
    return `${Math.round(number)}%`;
  }
  return formatRate(number);
}

/**
 * Merge API payloads over EMPTY_EMAIL_STATS.
 * Accepts null / undefined / {} / { totals: null } safely.
 */
export function normalizeEmailStats(raw) {
  const source = raw && typeof raw === 'object' ? raw : {};
  const totals = source.totals && typeof source.totals === 'object' ? source.totals : {};

  const softBounced = safeNumber(
    source.softBounced ?? source.soft_bounced ?? totals.softBounced ?? totals.soft_bounced,
  );
  const hardBounced = safeNumber(
    source.hardBounced ?? source.hard_bounced ?? totals.hardBounced ?? totals.hard_bounced,
  );
  const bounced = safeNumber(
    source.bounced ?? totals.bounced,
  ) || softBounced + hardBounced;

  const total = safeNumber(
    source.total ?? totals.total ?? source.sent ?? totals.sent ?? source.accepted ?? totals.accepted,
  );

  return {
    ...EMPTY_EMAIL_STATS,
    total,
    accepted: safeNumber(source.accepted ?? totals.accepted),
    rejected: safeNumber(source.rejected ?? totals.rejected),
    delivered: safeNumber(source.delivered ?? totals.delivered),
    opened: safeNumber(source.opened ?? totals.opened),
    clicked: safeNumber(source.clicked ?? totals.clicked),
    replied: safeNumber(source.replied ?? totals.replied),
    bounced,
    softBounced,
    hardBounced,
    unsubscribed: safeNumber(source.unsubscribed ?? totals.unsubscribed),
    complained: safeNumber(
      source.complained ?? source.spam_complaints ?? totals.complained ?? totals.spam,
    ),
    failed: safeNumber(source.failed ?? totals.failed),
    timeline: Array.isArray(source.timeline)
      ? source.timeline
      : (Array.isArray(source.series) ? source.series : []),
    recipients: Array.isArray(source.recipients) ? source.recipients : [],
  };
}

/** @deprecated Prefer normalizeEmailStats */
export function normalizeEmailStepStats(raw) {
  return normalizeEmailStats(raw);
}

export function getMetricDenominator(stats, rateOf) {
  if (!rateOf) return 0;
  return safeNumber(stats?.[rateOf]);
}

export function getMetricRate(stats, countKey, rateOf) {
  if (!rateOf) return 0;
  return safeRate(stats?.[countKey], getMetricDenominator(stats, rateOf));
}

export function getMetricDisplayValue(stats, def, showNumbers = false) {
  const count = safeNumber(stats?.[def.countKey]);
  if (showNumbers || !def.rateOf) {
    return formatCount(count);
  }
  return formatRate(getMetricRate(stats, def.countKey, def.rateOf));
}

export function buildEmailMetricCards(stats, { showNumbers = false, defs = EMAIL_METRIC_CARD_DEFS } = {}) {
  const data = normalizeEmailStats(stats);
  return defs.map((def) => ({
    id: def.id,
    label: def.label,
    count: safeNumber(data[def.countKey]),
    rate: def.rateOf ? getMetricRate(data, def.countKey, def.rateOf) : null,
    value: getMetricDisplayValue(data, def, showNumbers),
    kind: !def.rateOf || showNumbers ? 'count' : 'rate',
    viewDetails: Boolean(def.viewDetails),
  }));
}

export function buildEngagementSummary(stats, { showNumbers = false } = {}) {
  const data = normalizeEmailStats(stats);
  return EMAIL_ENGAGEMENT_SUMMARY_DEFS.map((def) => ({
    id: def.id,
    label: def.label,
    count: safeNumber(data[def.countKey]),
    rate: getMetricRate(data, def.countKey, def.rateOf),
    value: getMetricDisplayValue(data, def, showNumbers),
  }));
}

export function toIsoDateString(date) {
  const d = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(d.getTime())) return '';
  const year = d.getUTCFullYear();
  const month = String(d.getUTCMonth() + 1).padStart(2, '0');
  const day = String(d.getUTCDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

export function parseIsoDate(value) {
  if (!value) return null;
  const match = String(value).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) {
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }
  return new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3])));
}

export function eachDateInRange(startIso, endIso) {
  const start = parseIsoDate(startIso);
  const end = parseIsoDate(endIso);
  if (!start || !end) return [];
  const from = start <= end ? start : end;
  const to = start <= end ? end : start;
  const dates = [];
  const cursor = new Date(from.getTime());
  while (cursor <= to) {
    dates.push(toIsoDateString(cursor));
    cursor.setUTCDate(cursor.getUTCDate() + 1);
  }
  return dates;
}

export function defaultDateRange(days = 7) {
  const end = new Date();
  const start = new Date(end.getTime());
  start.setUTCDate(start.getUTCDate() - (Math.max(1, days) - 1));
  return {
    start: toIsoDateString(start),
    end: toIsoDateString(end),
  };
}

/**
 * Build chart buckets for every date in range.
 * Empty timeline still yields zero rows so the axis renders.
 */
export function buildTimelineBuckets(stats, dateRange) {
  const data = normalizeEmailStats(stats);
  const range = dateRange || defaultDateRange(7);
  const dates = eachDateInRange(range.start, range.end);
  const byDate = new Map();

  data.timeline.forEach((point) => {
    const key = toIsoDateString(point.date || point.label || point.day);
    if (!key) return;
    byDate.set(key, {
      date: key,
      delivered: safeNumber(point.delivered),
      opened: safeNumber(point.opened),
      clicked: safeNumber(point.clicked),
    });
  });

  return dates.map((date) => byDate.get(date) || {
    date,
    delivered: 0,
    opened: 0,
    clicked: 0,
  });
}

/** @deprecated Prefer buildTimelineBuckets */
export function buildZeroEngagementSeries(points = 7) {
  const range = defaultDateRange(points);
  return buildTimelineBuckets({}, range).map((row) => ({
    ...row,
    label: row.date,
    value: 0,
  }));
}

export function filterRecipientsByTab(recipients, tabId) {
  const rows = Array.isArray(recipients) ? recipients : [];
  if (!tabId) return rows;
  const needle = String(tabId).toLowerCase();
  return rows.filter((row) => {
    const status = String(row.status || row.event || '').toLowerCase().replace(/[_\s-]/g, '');
    const aliases = {
      delivered: ['delivered'],
      opened: ['opened', 'open'],
      clicked: ['clicked', 'click'],
      replied: ['replied', 'reply'],
      hardbounced: ['hardbounced', 'hardbounce'],
      softbounced: ['softbounced', 'softbounce'],
      complained: ['complained', 'complaint', 'spam'],
      unsubscribed: ['unsubscribed', 'unsubscribe'],
      failed: ['failed', 'failure'],
    };
    const matchers = aliases[needle] || [needle];
    return matchers.some((alias) => status.includes(alias));
  });
}

export function buildEmailStatsCsv(stats, recipients = [], tabId = 'delivered') {
  const headers = EMAIL_RECIPIENT_TABLE_HEADERS;
  const rows = filterRecipientsByTab(recipients, tabId).map((row) => [
    row.full_name || row.fullName || row.name || '',
    row.email || row.recipient || '',
    row.status || '',
    row.last_updated || row.updated_at || row.updated || '',
  ]);

  const escape = (cell) => {
    const text = String(cell ?? '');
    if (/[",\n]/.test(text)) return `"${text.replace(/"/g, '""')}"`;
    return text;
  };

  const lines = [
    headers.map(escape).join(','),
    ...rows.map((row) => row.map(escape).join(',')),
  ];

  return `${lines.join('\n')}\n`;
}

export function downloadEmailStatsCsv(filename, csvText) {
  if (typeof document === 'undefined') return;
  const blob = new Blob([csvText], { type: 'text/csv;charset=utf-8;' });
  const url = window.URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename || 'email-statistics.csv';
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  window.URL.revokeObjectURL(url);
}

export function formatChartAxisDate(isoDate) {
  const parsed = parseIsoDate(isoDate);
  if (!parsed) return isoDate;
  return parsed.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  });
}
